import type { IFetchError } from 'ofetch';
import type { FormOptions, SubmissionContext } from 'vee-validate';
import type { MaybeRef } from 'vue';
import { FetchError } from 'ofetch';
import { useForm } from 'vee-validate';
import { computed, isRef, ref, watch } from 'vue';
import { isDefined, parseApiErrorMessage } from '../utils/utils';
import { useToastError } from './toastError';

type GenericObject = Record<string, any>;

interface FormWrapperOptions<TValues extends GenericObject, TReturn> {

	/**
	 * Form options for the vee-validate form.
	 */
	form?: FormOptions<TValues>;

	/**
	 * A function that will be called when the form is submitted successfully.
	 */
	successCallback?: (data: TReturn) => void;
	customError?: MaybeRef<boolean>[];

	/**
	 * A function that will be called when an error occurs during submission.
	 * If the function returns true, the error will be considered handled and no further action will be taken.
	 */
	onError?: (error: IFetchError, ctx: SubmissionContext<TValues>) => boolean;

	toastError?: boolean;
}

export function useFormWrapper<TValues extends GenericObject = GenericObject, TReturn = unknown>(submit: (data: TValues, context: SubmissionContext) => Promise<TReturn>,	opts?: FormWrapperOptions<TValues, TReturn>) {
	const { form: formOptions, successCallback, customError, onError, toastError } = opts ?? {};

	const { displayError } = useToastError();
	const form = useForm<TValues>(formOptions);
	const error = ref<any>();

	const loading = ref(false);

	// const { t } = useI18n();
	const formShakeClass = ref<string>();

	const hasCustomError = computed(() => {
		if (!isDefined(customError)) {
			return false;
		}

		return customError.some(error => isRef(error) ? error.value : error);
	});

	watch(() => form.submitCount.value, () => {
		if ((form.meta.value.valid === false && formShakeClass.value === undefined) || hasCustomError.value) {
			formShakeClass.value = 'shakira';

			setTimeout(() => {
				formShakeClass.value = undefined;
			}, 500);
		}
	}, { deep: true });

	const submitFunction = form.handleSubmit(async (data, context) => {
		if (hasCustomError.value) {
			return;
		}

		loading.value = true;

		try {
			const returnData = await submit(data, context);

			if (successCallback) {
				successCallback(returnData);
			}

			return returnData;
		}
		catch (ex) {
			error.value = ex;

			if (typeof onError === 'function' && ex instanceof FetchError) {
				const shouldFinishErrorHandling = onError(ex, context);
				if (shouldFinishErrorHandling) {
					return;
				}
			}

			if (toastError === false) {
				return;
			}

			displayError(ex);
		}
		finally {
			loading.value = false;
		}
	});

	return {
		/**
		 * Indicates whether the form is submitting/loading.
		 */
		loading: computed(() => form.isSubmitting.value),

		/**
		 * Form reference.
		 */
		form,

		/**
		 * Validation function
		 */
		validate: form.validate,

		/**
		 * Submits the form.
		 */
		submit: submitFunction,

		/**
		 * Submission error.
		 */
		error: computed(() => error.value),

		/**
		 * Indicates whether there is an error.
		 */
		errorMsg: computed(() => error.value ? parseApiErrorMessage(error.value) : undefined),

		/**
		 * Return class for the form shake animation for when is submited with invalid data.
		 */
		formShakeClass,

		clearError: () => error.value = undefined,

		/**
		 * Indicates whether the submit button should be disabled.
		 */
		submitDisabled: computed(() => !form.meta.value.valid || !form.meta.value.dirty || hasCustomError.value),
	};
}
